import { type Expense, type ExpenseRequestTypeEnum, type ItemSplit, PriceSourceEnum, SplitTypeEnum, type Tax } from "@finway-group/shared/lib/models"
import { ItemSplitKindEnum } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { Col, Form, Row, Space } from "antd"
import { type FormInstance, type Rule } from "antd/lib/form"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { type DebouncedState } from "use-debounce/lib/useDebouncedCallback"
import { CurrencyInput } from "~Components/currencyInput"
import { type ExpenseData } from "~Components/modals/expenseCreateForm.context"
import { getDefaultTaxId } from "~Shared/utils/helper.utils"
import { type GetPopupAnchor } from "~Shared/utils/popup.utils"
import FormAmountsMismatchAlert from "./formAmountsMismatchAlert"
import { FormInputElem } from "./formInputAndTextarea"
import { FormItemCurrency } from "./formItemCurrency"
import FormItemSideButtons from "./formItemSideButtons"
import FormTaxRateSelector from "./formTaxRateSelector"
import { LineItemList } from "./lineItemList"

export interface FormAdditionalExpenseInterface {
    elem: {
        isHospitality: boolean
        expense: Expense
        taxValues: Array<Tax>
        onFieldUpdateDebounced: DebouncedState<(index: number, field: string, value: any) => void>
        onNetPriceUpdate: (index: number, netPrice: number) => void
        onTaxRateSplitUpdate: (index: number, taxRateId: string, priceSource: PriceSourceEnum) => void
        onTaxPriceUpdate: (index: number, taxPrice: number, priceSource: PriceSourceEnum) => void
        onGrossPriceUpdate: (index: number, grossPrice: number) => void
        getPopupAnchor: GetPopupAnchor
        isAmountsMismatch: boolean
        updateExpense: (data: ExpenseData) => void
        rules: { [key: string]: Array<Rule> }
        expenseForm: FormInstance
        isLinked: boolean
        isForcedSync: boolean
        type?: ExpenseRequestTypeEnum
        isNew: boolean
        isLoading?: boolean
    }
}

export const FormAdditionalExpense = ({ elem }: FormAdditionalExpenseInterface) => {
    const { t } = useTranslation()
    const {
        expense,
        taxValues,
        onFieldUpdateDebounced,
        onNetPriceUpdate,
        onTaxRateSplitUpdate,
        onTaxPriceUpdate,
        onGrossPriceUpdate,
        getPopupAnchor,
        isAmountsMismatch,
        updateExpense,
        isHospitality,
        rules,
        isLoading = false,
    } = elem
    const [priceSource, setPriceSource] = useState(PriceSourceEnum.GROSS_PRICE)

    const lineItemListProps = {
        expense,
        taxValues,
        onNetPriceUpdate,
        onTaxRateSplitUpdate: elem.onTaxRateSplitUpdate,
        onTaxPriceUpdate: elem.onTaxPriceUpdate,
        onGrossPriceUpdate: elem.onGrossPriceUpdate,
        getPopupAnchor,
        expenseForm: elem.expenseForm,
        isLinked: elem.isLinked,
        isForcedSync: elem.isForcedSync,
        updateExpense: elem.updateExpense,
        isLoading,
    }
    return (
        <>
            {expense.splitType === SplitTypeEnum.LINE_ITEM ? (
                <LineItemList {...lineItemListProps} />
            ) : (
                <Form.List name="splits">
                    {(fields, { add, remove }) => (
                        <div>
                            {/* In oneTimeExpense form, we only want to show the item form if the expense split type is ITEM. */}
                            {(!expense.splitType || expense.splitType === SplitTypeEnum.ITEM) &&
                                fields.map((field, index) => {
                                    /**
                                     * If its a hospitality type expense, it can have a tip
                                     * This row will:
                                     * - not have the item name editable (fixed to "Tip"/"Trinkgeld")
                                     * - not have the tax amount editable
                                     */
                                    const isTipRow: boolean = (expense.splits[index] as ItemSplit)?.kind === ItemSplitKindEnum.TIP
                                    const isSelfIssuedReceiptTaxLocked = !!expense.isSelfIssuedReceipt
                                    return (
                                        <Space key={index} className="flex mb-3" align="start">
                                            <Row data-testid="splitItemRow" key={index} gutter={[16, 16]} align="top">
                                                <FormInputElem
                                                    elem={{
                                                        name: [field.name, "name"],
                                                        label: t("input:request.item_name"),
                                                        key: "itemName",
                                                        rules: rules.name,
                                                        isRequired: !isTipRow,
                                                        placeholder: t("placeholder:request.item_name"),
                                                        onChange: (e) => onFieldUpdateDebounced.callback(index, "name", e.target.value),
                                                        isDisabled: isTipRow,
                                                    }}
                                                    span={6}
                                                    data-testid="itemNameInput"
                                                />
                                                <Col span={4}>
                                                    <FormItemCurrency
                                                        data-testid="inlineItemNetPrice"
                                                        currency={expense.currency}
                                                        name={[field.name, "netPrice"]}
                                                        label={t("input:request.net_price")}
                                                        key="netPrice"
                                                        required={!isTipRow}
                                                        rules={rules.totalNetPrice}
                                                    >
                                                        <CurrencyInput
                                                            currency={expense.currency}
                                                            value={0}
                                                            onChange={(value) => onNetPriceUpdate(index, value)}
                                                            onKeyDown={() => {
                                                                setPriceSource(PriceSourceEnum.NET_PRICE)
                                                            }}
                                                            isDisabled={isSelfIssuedReceiptTaxLocked}
                                                        />
                                                    </FormItemCurrency>
                                                </Col>
                                                <FormTaxRateSelector
                                                    data-testid="inlineItemVat"
                                                    elem={{
                                                        name: [field.name, "taxRate", "_id"],
                                                        label: t("input:request.tax_rate"),
                                                        key: "taxRate",
                                                        value: expense.splits[index]?.taxRate?._id ?? getDefaultTaxId(taxValues),
                                                        isRequired: !isTipRow && expense.isAtLeastApproved(),
                                                        className: "w-full text-xs md:text-base no-padding-select no-right-padding-select",
                                                        getPopupAnchor: getPopupAnchor(),
                                                        onChange: (value: string) => onTaxRateSplitUpdate(index, value, priceSource),
                                                        isDisabled: isTipRow || isSelfIssuedReceiptTaxLocked,
                                                        taxValues,
                                                    }}
                                                    span={4}
                                                />
                                                <Col span={4}>
                                                    <FormItemCurrency
                                                        data-testid="inlineItemVatPrice"
                                                        currency={expense.currency}
                                                        name={[field.name, "taxPrice"]}
                                                        label={t("input:request.tax_price")}
                                                        key="taxPrice"
                                                        rules={rules.totalTaxPrice}
                                                    >
                                                        <CurrencyInput
                                                            currency={expense.currency}
                                                            value={0}
                                                            onChange={(value) => onTaxPriceUpdate(index, value, PriceSourceEnum.NET_PRICE)}
                                                            isDisabled={isTipRow || isSelfIssuedReceiptTaxLocked}
                                                        />
                                                    </FormItemCurrency>
                                                </Col>
                                                <Col span={4}>
                                                    <FormItemCurrency
                                                        data-testid="inlineItemGrossPrice"
                                                        currency={expense.currency}
                                                        name={[field.name, "grossPrice"]}
                                                        label={t("input:request.gross_price")}
                                                        key="grossPrice"
                                                        required={!isTipRow}
                                                        rules={rules.totalGrossPrice}
                                                    >
                                                        <CurrencyInput
                                                            currency={expense.currency}
                                                            value={0}
                                                            onChange={(value) => onGrossPriceUpdate(index, value)}
                                                            onKeyDown={() => {
                                                                setPriceSource(PriceSourceEnum.GROSS_PRICE)
                                                            }}
                                                        />
                                                    </FormItemCurrency>
                                                </Col>

                                                <FormItemSideButtons
                                                    elem={{
                                                        fieldsLength: fields.length,
                                                        index,
                                                        onRemoveClick: remove,
                                                        updateExpense,
                                                        expense,
                                                        isHospitality,
                                                        isTipRow,
                                                        taxValues,
                                                        onAddClick: add,
                                                        isLoading,
                                                    }}
                                                    span={1}
                                                />
                                            </Row>
                                        </Space>
                                    )
                                })}
                        </div>
                    )}
                </Form.List>
            )}

            <FormAmountsMismatchAlert
                elem={{
                    isAmountsMismatch,
                    msgText: t("info:request_net_amount_mismatch"),
                    btnText: t("action:request.sync_now"),
                    updateExpense,
                    expense,
                    taxValues,
                }}
                span={24}
                md={24}
                sm={24}
            />
        </>
    )
}
